import React, { useEffect } from 'react'
import {
  ShieldCheck, Mail, Lock, UserCheck, Ban, Server, Phone, MapPin, FileText,
} from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { motion } from 'framer-motion'

// Trang Chính sách bảo mật — RIÊNG cho ebook.vatli365.vn (AI-PhysicsBook).
// Tham khảo bố cục chinh-sach-bao-mat của chuvanbien.vn.
const SEO_TITLE = 'Chính sách bảo mật | AI-PhysicsBook (ebook.vatli365.vn)'
const SEO_DESC =
  'Chính sách bảo mật của AI-PhysicsBook (ebook.vatli365.vn): chúng tôi chỉ thu thập địa chỉ email khi bạn đăng nhập bằng Google, không chia sẻ cho bên thứ ba.'

// Các nội dung của website này nằm trong phạm vi áp dụng
const DOMAINS = [
  { name: 'Soạn bài giảng tương tác', desc: 'Tạo & lưu bài giảng bằng AI' },
  { name: 'Khoá học trực tuyến', desc: 'Ghi danh & theo dõi tiến độ' },
  { name: 'Thi online', desc: 'Làm bài & lưu kết quả luyện tập' },
  { name: 'Mô phỏng & thí nghiệm', desc: 'Học Vật lí trực quan' },
]

// Mỗi mục = 1 thẻ chính sách
const SECTIONS: { icon: React.ElementType; title: string; body: React.ReactNode }[] = [
  {
    icon: Mail,
    title: '1. Chúng tôi thu thập thông tin gì?',
    body: (
      <>
        <p>
          AI-PhysicsBook (ebook.vatli365.vn) hoạt động trên cơ chế đăng nhập bằng <strong>Tài khoản Google</strong>.
          Khi bạn đăng nhập, chúng tôi <strong>chỉ thu thập địa chỉ email</strong> của bạn (kèm tên hiển thị
          và ảnh đại diện do Google cung cấp) để tạo và nhận diện tài khoản.
        </p>
        <p>
          Chúng tôi <strong>không yêu cầu mật khẩu Google</strong>, không truy cập danh bạ, email hay
          bất kỳ dữ liệu cá nhân nào khác trong tài khoản Google của bạn.
        </p>
        <p>
          Trong quá trình sử dụng, hệ thống lưu các dữ liệu phục vụ học tập do chính bạn tạo ra
          (bài giảng, đề thi, kết quả luyện tập, tiến độ học) và gắn với email của bạn.
        </p>
      </>
    ),
  },
  {
    icon: Server,
    title: '2. Chúng tôi dùng thông tin để làm gì?',
    body: (
      <>
        <p>Địa chỉ email và dữ liệu học tập chỉ được dùng cho các mục đích vận hành sản phẩm:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Tạo, đăng nhập và bảo vệ tài khoản của bạn.</li>
          <li>Lưu và đồng bộ nội dung bạn tạo (bài giảng, khoá học, đề thi, kết quả, tiến độ).</li>
          <li>Cung cấp các tính năng theo gói tài khoản (miễn phí / PRO) và hỗ trợ kỹ thuật khi bạn liên hệ.</li>
          <li>Gửi thông báo cần thiết liên quan đến tài khoản và dịch vụ (nếu có).</li>
        </ul>
        <p>Chúng tôi <strong>không dùng email của bạn cho mục đích quảng cáo của bên ngoài</strong>.</p>
      </>
    ),
  },
  {
    icon: Ban,
    title: '3. Chia sẻ thông tin cho bên thứ ba',
    body: (
      <>
        <p>
          Chúng tôi <strong>cam kết không bán, không trao đổi và không chia sẻ</strong> địa chỉ email
          cũng như dữ liệu của bạn cho bất kỳ bên thứ ba nào vì mục đích thương mại.
        </p>
        <p>
          Thông tin chỉ có thể được cung cấp trong trường hợp <strong>bắt buộc theo quy định của pháp luật</strong>
          hoặc theo yêu cầu hợp pháp của cơ quan nhà nước có thẩm quyền.
        </p>
      </>
    ),
  },
  {
    icon: Lock,
    title: '4. Bảo mật & lưu trữ',
    body: (
      <>
        <p>
          Dữ liệu được truyền qua kết nối mã hoá (HTTPS) và lưu trên máy chủ của hệ thống. Chúng tôi
          áp dụng các biện pháp kỹ thuật hợp lý để bảo vệ thông tin khỏi truy cập trái phép.
        </p>
        <p>
          Vì đăng nhập qua Google, bạn nên tự bảo vệ Tài khoản Google của mình và đăng xuất khi dùng
          máy tính chung. Dữ liệu được lưu trong thời gian tài khoản còn hoạt động.
        </p>
      </>
    ),
  },
  {
    icon: UserCheck,
    title: '5. Quyền của bạn',
    body: (
      <>
        <p>Bạn có quyền:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Yêu cầu xem, chỉnh sửa nội dung do mình tạo trong tài khoản.</li>
          <li>Yêu cầu xoá tài khoản và dữ liệu liên quan bằng cách liên hệ với chúng tôi.</li>
        </ul>
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: '6. Chấp thuận chính sách',
    body: (
      <>
        <p>
          Khi bạn <strong>đăng nhập bằng Tài khoản Google</strong> vào AI-PhysicsBook (ebook.vatli365.vn),
          điều đó đồng nghĩa với việc bạn <strong>đã đọc và đồng ý</strong> với Chính sách bảo mật này.
        </p>
        <p>
          Khi chính sách có thay đổi, phiên bản mới sẽ được cập nhật ngay trên trang này.
        </p>
      </>
    ),
  },
]

const ChinhSachBaoMat: React.FC = () => {
  useEffect(() => {
    const prevTitle = document.title
    document.title = SEO_TITLE
    const meta = document.querySelector('meta[name="description"]')
    const prevDesc = meta?.getAttribute('content') || ''
    meta?.setAttribute('content', SEO_DESC)
    return () => {
      document.title = prevTitle
      meta?.setAttribute('content', prevDesc)
    }
  }, [])

  return (
    <div className="min-h-screen bg-transparent">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-10">
        {/* Tiêu đề */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <span className="inline-flex w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 items-center justify-center mb-5">
            <ShieldCheck className="w-8 h-8" />
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 leading-tight">
            Chính sách bảo mật
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Chính sách này áp dụng cho <strong>AI-PhysicsBook (ebook.vatli365.vn)</strong>. Chúng tôi
            tôn trọng quyền riêng tư của bạn: chỉ thu thập <strong>địa chỉ email</strong> khi đăng nhập
            bằng Google và <strong>không chia sẻ cho bên thứ ba</strong>.
          </p>
        </motion.header>

        {/* Phạm vi áp dụng */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          className="bg-white/80 backdrop-blur rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8"
        >
          <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" /> Phạm vi áp dụng
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {DOMAINS.map((d) => (
              <div key={d.name} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-400" />
                <span>
                  <p className="font-semibold text-slate-800 text-sm">{d.name}</p>
                  <p className="text-xs text-slate-500">{d.desc}</p>
                </span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Các mục chính sách */}
        <div className="space-y-5">
          {SECTIONS.map((s, i) => {
            const Icon = s.icon
            return (
              <motion.section
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                className="bg-white/80 backdrop-blur rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8"
              >
                <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2.5">
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </span>
                  {s.title}
                </h2>
                <div className="space-y-3 text-slate-600 leading-relaxed pl-0 md:pl-[3.1rem]">
                  {s.body}
                </div>
              </motion.section>
            )
          })}
        </div>

        {/* Liên hệ */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-7 md:p-9 text-white"
        >
          <h2 className="text-xl font-bold mb-4 text-white">Liên hệ về quyền riêng tư</h2>
          <ul className="space-y-2.5 text-slate-200 text-sm">
            <li className="flex items-center gap-2.5"><UserCheck className="w-4 h-4 text-slate-400" /> Thầy Trần Quốc Quân — GV Vật lí, THPT Tây Sơn</li>
            <li className="flex items-center gap-2.5"><MapPin className="w-4 h-4 text-slate-400" /> Bình An, Gia Lai</li>
            <li className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-slate-400" /> <a href="tel:0978531191" className="hover:text-white">0978 531 191</a></li>
            <li className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-slate-400" /> <a href="mailto:quants82@gmail.com" className="hover:text-white">quants82@gmail.com</a></li>
          </ul>
        </motion.section>
      </div>

      <Footer />
    </div>
  )
}

export default ChinhSachBaoMat
